import { useCallback, useContext, useEffect, useState } from "react";
import UserContext from "../Contexts/UserContext";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCancel, faCheck } from "@fortawesome/free-solid-svg-icons";
import { default as axios } from "../axiosConfig";
import { DayPicker } from "react-day-picker";
import useFetchHolidays from "../Hooks/useFetchHolidays";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-day-picker/style.css";
import {
  API_CreateLeaveRequest,
  API_DeleteLeaveRequest,
  API_UpdateLeaveRequest,
  API_GetCandidateByID,
  API_GetCompanyIDByJob,
  API_GetCompanyInfo,
  API_GetJobContract,
  API_GetHMInfo,
  API_GetCompanyPosting,
  API_GetRegion,
  API_ApproveLeaveRequest,
  API_RejectLeaveRequest,
  API_ResubmitLeaveRequest,
  API_CheckDuplicateLeaveDates,
} from "../Assets/ApiUrlContainer";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isoWeek);

const LeaveRequestDetails = ({
  leaveRequest,
  managerId,
  onBack,
  candidateId,
  jobId,
  jobName,
  isTGS,
  onClose,
  readOnlyCalendar,
}) => {
  const isLeaveRequestEmpty = !leaveRequest || Object.keys(leaveRequest).length === 0;
  const { user } = useContext(UserContext);
  const [location, setLocation] = useState("");
  const [companyName, setCompanyName] = useState("Loading...");
  const [employeeName, setEmployeeName] = useState("");
  const [managerName, setManagerName] = useState("");
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [hoursPerDay, setHoursPerDay] = useState({});
  const [typeOfLeave, setTypeOfLeave] = useState("Vacation");
  const [employerComments, setEmployerComments] = useState("");
  const [employeeComments, setEmployeeComments] = useState("");
  const [errorMessage, setErrorMessage] = useState(""); // Show error message
  const [maxHoursPerDay, setMaxHoursPerDay] = useState(7.5);
  const [showJuryDutyWarning, setShowJuryDutyWarning] = useState(false);
  const { holidays } = useFetchHolidays(location);
  const [isProcessed, setIsProcessed] = useState(false);
  const [localStatus, setLocalStatus] = useState(leaveRequest.Status);
  const [showApprovalMessage, setShowApprovalMessage] = useState(false);
  const [showRejectionMessage, setShowRejectionMessage] = useState(false);
  const [showResubmittedMessage, setShowResubmittedMessage] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);
  const [disabledDates, setDisabledDates] = useState([]);
  const [hiringManagerID, setHiringManagerID] = useState(null);

  const userIsTgsAdmin = user.Role === "tgs-admin";

  const convertDateForDayJS = (dateToConvert) => {
    let date = new Date(dateToConvert);
    return `${date.getUTCFullYear()}-${("0" + (date.getUTCMonth() + 1)).slice(-2)}-${("0" + date.getUTCDate()).slice(-2)}`;
  }

  // min and max dates for calendar selection range limits
  const minDate = dayjs().subtract(1, "year").toDate();
  const maxDate = dayjs().add(1, "year").toDate();

  const editable = !managerId && localStatus !== "approved" && (
    !leaveRequest.Id || (
      !leaveRequest.ApprovingManagerId && localStatus !== "submitted" && (
        !leaveRequest.StartDate || dayjs(convertDateForDayJS(leaveRequest.StartDate)).isAfter(dayjs(), "day") || (
          leaveRequest.EmployerNotes && !leaveRequest.ApprovingManagerId
        )
      )
    )
  );

  const fetchCandidateInfo = useCallback(async () => {
    try {
      const candidateResponse = await axios.get(`${API_GetCandidateByID}/${candidateId}`);
      setEmployeeName(candidateResponse.data.Name || "Unknown Employee");

      const companyIDResponse = await axios.get(`${API_GetCompanyIDByJob}/${jobId}`);
      const companyId = companyIDResponse.data.CompanyId;

      const companyInfoResponse = await axios.get(`${API_GetCompanyInfo}/${companyId}`);
      const companyInfo = companyInfoResponse.data;

      setCompanyName(companyInfo?.Name || "Unknown Company");

      const jobContractResponse = await axios.get(API_GetJobContract, {
        params: {
          CandidateId: candidateId,
          JobId: jobId,
        },
      });

      setMaxHoursPerDay(jobContractResponse.data.DailyHoursLimit);

      const companyPostingResponse = await axios.get(`${API_GetCompanyPosting}/${jobId}`);
      const id = companyPostingResponse.data.ManagerId;

      if (user.Role === "hiring manager") {
        setHiringManagerID(id);
      }

      const HMInfoResponse = await axios.get(`${API_GetHMInfo}/${id}`);
      setManagerName(HMInfoResponse.data.Name);

      const employeeLocationResponse = await axios.get(`${API_GetRegion}/${candidateId}`);
      setLocation(employeeLocationResponse.data.region);

    } catch (error) {
      console.error("Error fetching candidate info:", error);
    }
  }, [candidateId, jobId]);

  const fetchDisabledDates = useCallback(async () => {
    try {
      const res = await axios.post(API_CheckDuplicateLeaveDates, {
        CandidateId: candidateId,
        JobId: jobId,
        StartDate: range.from ? dayjs(range.from).format("YYYY-MM-DD") : null,
        EndDate: range.to ? dayjs(range.to).format("YYYY-MM-DD") : null,
        LeaveId: leaveRequest?.Id || null,
      });

      if (res.data?.overlappingDates?.length > 0) {
        const formatted = res.data.overlappingDates.map((dateStr) => {
          const dateObj = new Date(dateStr + "T00:00:00");
          return dateObj;
        });

        setDisabledDates(formatted);
      } else {
        setDisabledDates([]);
      }
    } catch (error) {
      console.error("Failed to fetch disabled leave dates:", error);
    }
  }, [candidateId, jobId, leaveRequest?.Id, range]);

  // useEffect(() => {
  //   const fetchHiringManagerID = async () => {
  //     if (user.Role === "hiring manager" && user.Email) {
  //       try {
  //         const res = await axios.get(API_GetHiringManagerByEmail, {
  //           params: { email: user.Email },
  //         });

  //         if (res.data && res.data.ManagerId) {
  //           setHiringManagerID(res.data.ManagerId);
  //           console.log("hiringManagerID fetched:", res.data.ManagerId);
  //         }
  //       } catch (err) {
  //         console.error("Failed to fetch hiring manager Id:", err);
  //       }
  //     }
  //   };
  //   fetchHiringManagerID();
  // }, [user]);

  const isHoliday = useCallback(
    (date) => {
      const formatted = dayjs(date).format("YYYY-MM-DD");
      const match = holidays.find((h) => h.date === formatted);
      return match?.name || null;
    }, [holidays]
  );

  const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

  useEffect(() => {
    fetchCandidateInfo();
  }, [jobId, candidateId, fetchCandidateInfo]);

  useEffect(() => {
    fetchDisabledDates();
  }, [fetchDisabledDates]);

  useEffect(() => {
    // Fill fields with saved data from existing leave request
    if (leaveRequest.Id) {
      setRange({
        from: dayjs(convertDateForDayJS(leaveRequest.StartDate)).toDate(),
        to: dayjs(convertDateForDayJS(leaveRequest.EndDate)).toDate(),
      });

      const parsed = JSON.parse(leaveRequest.HoursOffPerDay);
      setHoursPerDay(parsed);
      setTypeOfLeave(leaveRequest.TypeOfLeave);
      setEmployeeComments(leaveRequest.EmployeeNotes);
      setEmployerComments(leaveRequest.EmployerNotes);
    }
  }, [leaveRequest]);

  // set/reset hours per day array if date range changes
  useEffect(() => {
    if (!range.from || !range.to) return;

    const updatedHours = {};
    const startDate = dayjs(range.from);
    const endDate = dayjs(range.to);
    let current = startDate;

    while (current.isSameOrBefore(endDate)) {
      const isWeekendDay = isWeekend(current.toDate());
      const formatted = current.format("MM-DD");

      // if it is a weekday
      if (!isWeekendDay) {
        // if it is a holiday, no time off is taken
        if (isHoliday(current)) {
          updatedHours[formatted] = 0;
        } else {
          // otherwise, fill existing fields or set to default
          updatedHours[formatted] = hoursPerDay[formatted] ?? maxHoursPerDay;
        }
      }
      current = current.add(1, "day");
    }

    // Only update state if something actually changed to prevent infinite loops
    const updatedKeys = Object.keys(updatedHours);
    const currentKeys = Object.keys(hoursPerDay);
    const isDifferent =
      updatedKeys.length !== currentKeys.length ||
      updatedKeys.some((key) => hoursPerDay[key] !== updatedHours[key]);

    if (isDifferent) {
      setHoursPerDay(updatedHours);
    }
  }, [hoursPerDay, maxHoursPerDay, range, isHoliday]);

  useEffect(() => {
    if (leaveRequest) {
      setIsProcessed(
        leaveRequest.ApprovingManagerId !== null &&
        leaveRequest.ApprovingManagerId !== undefined
      );
    }
  }, [leaveRequest]);

  const handleSubmit = async (approving = false) => {
    try {
      if (hasOverlap()) {
        toast.error("You have selected dates that overlap with another approved leave request.");
        return;
      }

      if (managerId) {
        if (!approving && !employerComments) {
          setErrorMessage("Notes section must not be blank if rejecting request!");
          return;
        }

        // clear error message
        setErrorMessage("");

        await axios.put(`${API_UpdateLeaveRequest}/${leaveRequest.Id}`, {
          ...leaveRequest,
          StartDate: range.from ? dayjs(range.from).format("YYYY-MM-DD") : null,
          EndDate: range.to ? dayjs(range.to).format("YYYY-MM-DD") : null,
          SubmittedDate: dayjs().format("YYYY-MM-DD"),
          ApprovingManagerId: approving && !isTGS ? managerId : (!approving ? null : leaveRequest.ApprovingManagerId),
          ApprovingTgsManagerId: approving && isTGS ? managerId : null,
          EmployerNotes: employerComments,
        });
      }

      // if leave request already exists, update it
      else if (leaveRequest.Id) {
        await axios.put(`${API_UpdateLeaveRequest}/${leaveRequest.Id}`, {
          ...leaveRequest,
          StartDate: range.from ? dayjs(range.from).format("YYYY-MM-DD") : null,
          EndDate: range.to ? dayjs(range.to).format("YYYY-MM-DD") : null,
          HoursOffPerDay: JSON.stringify(hoursPerDay),
          TotalHoursOff: Object.values(hoursPerDay).reduce((sum, h) => sum + Number(h || 0), 0),
          TypeOfLeave: typeOfLeave,
          EmployeeNotes: employeeComments,
          SubmittedDate: dayjs().format("YYYY-MM-DD"),
          ApprovingManagerId: null,
          ApprovingTgsManagerId: null,
        });

      } else {
        // otherwise, create it
        await axios.post(API_CreateLeaveRequest, {
          CandidateId: candidateId,
          StartDate: range.from ? dayjs(range.from).format("YYYY-MM-DD") : null,
          EndDate: range.to ? dayjs(range.to).format("YYYY-MM-DD") : null,
          HoursOffPerDay: JSON.stringify(hoursPerDay),
          TotalHoursOff: Object.values(hoursPerDay).reduce((sum, h) => sum + Number(h || 0), 0),
          JobId: jobId,
          TypeOfLeave: typeOfLeave,
          EmployeeNotes: employeeComments,
          SubmittedDate: dayjs().format("YYYY-MM-DD"),
        });
      }
    } catch (error) {
      console.error("Error creating/updating leave request:", error);
    }
    onBack();
  };

  const handleCancel = async () => {
    try {
      const isDraft =
        !leaveRequest.SubmittedDate &&
        !leaveRequest.ApprovingManagerId &&
        !leaveRequest.ApprovingTgsManagerId;

      if (isDraft && leaveRequest.Id) {
        await axios.delete(`${API_DeleteLeaveRequest}/${leaveRequest.Id}`);
      }
    } catch (error) {
      console.error("Error deleting leave request:", error);
    }

    onBack(); // Always navigate back
  };

  const handleApprove = async () => {
    try {
      const res = await axios.post(`${API_ApproveLeaveRequest}/${leaveRequest.Id}`, {
        approverRole: user?.Role === "hiring manager" ? "manager" : user?.Role,
        approverId: hiringManagerID,
      });

      if (res.status === 200) {
        toast.success("Leave request approved successfully.");
        setIsProcessed(true);
        setLocalStatus("approved");
        setShowApprovalMessage(true);
        if (onClose) onClose();
      }
    } catch (error) {
      console.error("Error approving leave request:", error);
      toast.error("Failed to approve leave request");
    }
  };

  const handleReject = async () => {
    if (!employerComments || employerComments.trim() === "") {
      setErrorMessage("Manager Notes must not be blank to reject the request.");
      return;
    }
    try {
      const res = await axios.post(`${API_RejectLeaveRequest}/${leaveRequest.Id}`, {
        managerNotes: employerComments,
        rejectorId: hiringManagerID,
        rejectorRole: user?.Role === "hiring manager" ? "manager" : user?.Role,
      });

      if (res.status === 200) {
        toast.success("Leave request rejected successfully");
        setIsProcessed(true);
        setLocalStatus("rejected");
        setShowRejectionMessage(true);
        if (onClose) onClose(); // optional, remove if it auto-navigates away
      }
    } catch (error) {
      console.error("Error rejecting leave request:", error);
      toast.error("Failed to reject leave request");
    }
  };

  const handleResubmit = async () => {
    try {
      if (hasOverlap()) {
        toast.error(
          "You have selected dates that overlap with another approved leave request."
        );
        return;
      }

      const res = await axios.post(`${API_ResubmitLeaveRequest}/${leaveRequest.Id}`, {
        startDate: dayjs(range.from).format("YYYY-MM-DD"),
        endDate: dayjs(range.to).format("YYYY-MM-DD"),
        submittedDate: dayjs().format("YYYY-MM-DD"),
        totalHrsOff: Object.values(hoursPerDay).reduce((sum, h) => sum + Number(h || 0), 0),
        hrsOffPerDay: JSON.stringify(hoursPerDay),
        leaveType: typeOfLeave,
        employeeNotes: employeeComments,
      });

      if (res.status === 200) {
        toast.success("Leave request resubmitted successfully");
        setIsProcessed(true);
        setLocalStatus("submitted");
        setShowResubmittedMessage(true);
      }
    } catch (error) {
      console.error("Error resubmitting leave request:", error);
      toast.error("Failed to resubmit leave request");
    }
  };

  const checkForDuplicateDates = async () => {
    try {
      const res = await axios.post(API_CheckDuplicateLeaveDates, {
        CandidateId: candidateId,
        JobId: jobId,
        StartDate: dayjs(range.from).format("YYYY-MM-DD"),
        EndDate: dayjs(range.to).format("YYYY-MM-DD"),
        LeaveId: leaveRequest?.Id || null,
      });
      return res.data.hasOverlap;
    } catch (error) {
      console.error("Error checking duplicate leave dates:", error);
      return false;
    }
  };

  const hasOverlap = () => {
    if (!range?.from || !range?.to) return false;

    const selectedDates = [];
    let current = dayjs(range.from);

    while (current.isSameOrBefore(dayjs(range.to))) {
      selectedDates.push(current.toDate());
      current = current.add(1, "day");
    }

    return selectedDates.some((date) =>
      disabledDates.some((disabledDate) =>
        dayjs(disabledDate).isSame(date, "day")
      )
    );
  };

  const generateDayInputs = () => {
    if (!range.from || !range.to) return null;

    const startDate = dayjs(range.from).startOf("week"); // Start on Sunday
    const endDate = dayjs(range.to).endOf("week"); // End on Saturday
    const weeks = [];
    let current = startDate;

    while (current.isSameOrBefore(endDate)) {
      const days = [];

      for (let i = 0; i < 7; i++) {
        const formatted = current.format("MM-DD");
        const holidayName = isHoliday(current);
        const isWeekendDay = current.day() === 0 || current.day() === 6;
        const isInRange =
          dayjs(current).isSameOrAfter(dayjs(range.from), "day") &&
          dayjs(current).isSameOrBefore(dayjs(range.to), "day");

        const cellContent = () => {
          if (!isInRange || isWeekendDay) {
            return (
              <td
                key={formatted}
                style={{
                  backgroundColor: "#f9f9f9",
                  height: "60px",
                  borderRadius: "8px",
                  verticalAlign: "top",
                }}
              />
            );
          }

          const isLeaveRequestEmpty =
            !leaveRequest || Object.keys(leaveRequest).length === 0;

          return (
            <td
              key={formatted}
              style={{ verticalAlign: "top", padding: "4px" }}
            >
              <TextField
                label={
                  current.format("MMM D") +
                  (holidayName ? ` (${holidayName})` : "")
                }
                type="number"
                value={
                  holidayName
                    ? ""
                    : hoursPerDay[formatted] === undefined
                      ? maxHoursPerDay
                      : hoursPerDay[formatted]
                }
                disabled={!!holidayName || !editable}
                onChange={(e) => {
                  const input = e.target.value;
                  setHoursPerDay((prev) => ({
                    ...prev,
                    [formatted]:
                      input === ""
                        ? ""
                        : Math.min(Math.max(Number(input), 0), maxHoursPerDay),
                  }));
                }}
                fullWidth
                inputProps={{
                  min: 0,
                  max: maxHoursPerDay,
                  readOnly: !!holidayName, // prevents autofill/content rendering
                }}
                sx={{
                  borderRadius: "8px",
                  backgroundColor: holidayName ? "#f0f0f0" : "white",
                  "& .MuiInputBase-input.Mui-disabled": {
                    color: holidayName ? "transparent" : "inherit", // hide text
                  },
                  "& .MuiInputLabel-root.Mui-disabled": {
                    color: "#999", // label still readable
                  },
                }}
              />
            </td>
          );
        };

        days.push(cellContent());
        current = current.add(1, "day");
      }

      weeks.push(<tr key={current.format("YYYY-WW")}>{days}</tr>);
    }

    return (
      <Box sx={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: "8px",
          }}
        >
          <thead>
            <tr>
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <th
                  key={day}
                  style={{ textAlign: "left", paddingBottom: "8px" }}
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{weeks}</tbody>
        </table>
      </Box>
    );
  };

  if (isLeaveRequestEmpty) {
    return (
      <div className="">
        <div className="tasksContent">
          <p>Loading leave request...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="tasksContent">
        <div className="actionBar">
          <Button
            variant="outlined"
            sx={{ borderColor: "#673ab7", color: "#673ab7" }}
            onClick={onBack}
          >
            Back to All Leave Requests
          </Button>
          <div className="tasksBtnContainer">
            {/* if manager & not already TGS approved, approval and rejection buttons */}
            {/* {managerId && !leaveRequest.ApprovingTgsManagerId && localStatus !== "approved" && localStatus !== "rejected" && localStatus !== "archived" ? ( */}
            {managerId && !leaveRequest.ApprovingTgsManagerId && localStatus !== "rejected" && localStatus !== "archived" ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ marginRight: "5px" }}
                  onClick={handleApprove}
                >
                  <FontAwesomeIcon
                    icon={faCheck}
                    style={{ marginRight: "5px" }}
                  />
                  Approve Request
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  onClick={handleReject}
                >
                  <FontAwesomeIcon
                    icon={faCancel}
                    style={{ marginRight: "5px" }}
                  />
                  Reject Request
                </Button>
              </>
            ) : null}
          </div>
        </div>

        {/* Employer Notes Section */}
        {(leaveRequest.EmployerNotes || managerId) && (
          <TextField
            label="Manager Notes"
            multiline
            rows={3}
            fullWidth
            value={employerComments}
            onChange={(e) => {
              setEmployerComments(e.target.value);
              if (e.target.value.trim()) {
                setErrorMessage(""); // Clear error if notes are entered
              }
            }}
            placeholder="Add any comments here..."
            disabled={
              !managerId ||
              (localStatus === "approved" && leaveRequest.ApprovingManagerId && leaveRequest.ApprovingTgsManagerId) ||
              localStatus === "rejected"
            }
          />
        )}

        {showApprovalMessage && (
          <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
            Leave Request Approved!
          </Alert>
        )}

        {showRejectionMessage && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            Leave Request Rejected.
          </Alert>
        )}

        {showResubmittedMessage && (
          <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
            Leave Request Resubmitted!
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {/* Employee Info Section */}
        <div className="headerContainer">
          <div className="employeeInfo">
            <p>
              <strong>Name:</strong> {employeeName || "Loading..."}
            </p>
            <p>
              <strong>Company:</strong> {companyName || "Loading..."}
            </p>
            <p>
              <strong>Job: </strong>
              {jobName || "Loading..."}
            </p>
            <p>
              <strong>Manager: </strong>
              {managerName || "Loading..."}
            </p>
            <p>
              <strong>Maximum Hours Per Day: </strong>
              {maxHoursPerDay}
            </p>
          </div>
          <div className="projectInfo">
            <p>
              <strong>Submission Date: </strong>
              {dayjs(convertDateForDayJS(leaveRequest.SubmittedDate)).format("MMM D, YYYY")}
            </p>
            <p>
              <strong>Status: </strong>
              <span
                style={{
                  color:
                    localStatus === "approved"
                      ? "green"
                      : localStatus === "rejected"
                        ? "red"
                        : "inherit",
                }}
              >
                {localStatus === "approved"
                  ? isTGS && leaveRequest.ApprovingTgsManagerId
                    ? "Approved by TGS"
                    : "Approved by Manager"
                  : localStatus === "rejected"
                    ? "Rejected"
                    : localStatus === "submitted"
                      ? "Pending Approval"
                      : localStatus === "archived"
                        ? "Archived"
                        : localStatus === "cancelled"
                          ? "Cancelled"
                          : "Draft"}
              </span>
            </p>

            <p>
              <strong>Timeframe: </strong>
              {range?.from && range?.to
                ? `${dayjs(range.from).format("MMM D, YYYY")} - ${dayjs(
                  range.to
                ).format("MMM D, YYYY")}`
                : "N/A"}
            </p>
            <p>
              <strong>Total Hours: </strong>
              {Object.values(hoursPerDay).reduce(
                (sum, h) => sum + Number(h || 0),
                0
              )}
            </p>
          </div>
        </div>

        <Box className="calendarRangePicker" sx={{ mt: 2 }}>
          <DayPicker
            mode="range"
            selected={
              range && typeof range === "object" && "from" in range
                ? range
                : { from: undefined, to: undefined }
            }
            onSelect={readOnlyCalendar ? undefined : setRange}
            numberOfMonths={3}
            defaultMonth={dayjs().subtract(1, "month").toDate()}
            disabled={
              readOnlyCalendar
                ? [{ before: new Date(2100, 0, 1) }] // disables everything
                : managerId
                  ? [{ before: new Date(0) }]
                  : [
                    { before: minDate },
                    { after: maxDate },
                    ...disabledDates.map((date) => ({ date })),
                  ]
            }
            modifiers={{
              blocked: disabledDates,
            }}
            modifiersStyles={{
              blocked: {
                backgroundColor: "#fddede",
                color: "#d32f2f",
                textDecoration: "line-through",
              },
            }}
          />
        </Box>

        {range.from && range.to && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 3 }}>
              Hours Off (Per Day){" "}
              <Box component="span" sx={{ color: "error.main" }}>
                *
              </Box>
            </Typography>
            {generateDayInputs()}
            {/* Leave Type Dropdown */}
            <Box sx={{ mt: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Type of Leave{" "}
                <Box component="span" sx={{ color: "error.main" }}>
                  *
                </Box>
              </Typography>
              <Select
                value={typeOfLeave || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setTypeOfLeave(val);
                  setShowJuryDutyWarning(val === "Jury Duty");
                }}
                fullWidth
                size="small"
                displayEmpty
                sx={{ backgroundColor: "white" }}
                disabled={!editable}
              >
                <MenuItem value="" disabled>
                  Select a leave type
                </MenuItem>
                <MenuItem value="Vacation">Vacation</MenuItem>
                <MenuItem value="Parental">Parental</MenuItem>
                <MenuItem value="Bereavement">Bereavement</MenuItem>
                <MenuItem value="Sick">Sick</MenuItem>
                <MenuItem value="Jury Duty">Jury Duty</MenuItem>
                <MenuItem value="Medical">Medical</MenuItem>
                <MenuItem value="Unpaid">Unpaid</MenuItem>
                <MenuItem value="Non-Statutory Holiday">
                  Non-Statutory Holiday
                </MenuItem>
              </Select>
              {showJuryDutyWarning && (
                <Typography variant="body2" sx={{ color: "error.main", mt: 1 }}>
                  Note: Please ensure you email supporting documents to your
                  manager.
                </Typography>
              )}
            </Box>
            <TextField
              label="Employee/Contractor Comments"
              multiline
              rows={4}
              fullWidth
              value={employeeComments}
              onChange={(e) => setEmployeeComments(e.target.value)}
              placeholder="Add any context or notes about your leave request..."
              disabled={!editable}
            />

            {hasOverlap() && (
              <Alert severity="error" sx={{ mt: 2 }}>
                You've selected dates that overlap with an existing approved
                leave request. Please choose a different range.
              </Alert>
            )}
          </>
        )}

        {/* If user is manager, or start date has already passed, or manager has already approved, no buttons*/}

        {!editable ? null : (
          <>
            <Button
              onClick={
                leaveRequest.EmployerNotes ? handleResubmit : handleSubmit
              }
              disabled={!range.from || !range.to || isProcessed || hasOverlap()}
              variant="contained"
              sx={{ mt: 2, backgroundColor: "#673ab7" }}
            >
              {leaveRequest.EmployerNotes
                ? "Resubmit Leave Request"
                : leaveRequest.Id
                  ? "Update Leave Request"
                  : "Submit Leave Request"}
            </Button>
            <Button
              onClick={handleCancel}
              disabled={
                !leaveRequest ||
                leaveRequest.SubmittedDate ||
                leaveRequest.ApprovingManagerId ||
                leaveRequest.ApprovingTgsManagerId
              }
              variant="outlined"
              sx={{
                mt: 2,
                ml: 2,
                borderColor: "#d32f2f",
                color: "#d32f2f",
                "&:hover": {
                  backgroundColor: "#fddede",
                  borderColor: "#b71c1c",
                },
              }}
            >
              Cancel Leave Request
            </Button>
          </>
        )}
      </div>
    </div>
    // </div>
  );
};

export default LeaveRequestDetails;
